import {
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakService } from 'keycloak-angular';
import { DateTime } from 'luxon';
import {
  BsModalRef,
  BsModalService,
} from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import {
  AppService,
  LayoutService,
} from 'src/app/services';
import { environment } from 'src/environments/environment';
import {
  InfoPageDialogComponent,
  InfoPageTemplate,
} from '../dialogs';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  tenant!: string;
  isLandingPage!: boolean;
  linkToContactPage!: string;
  linkToWithdrawalPage!: string;
  yearNow!: string;

  private unsubOnDestroy: Subscription = new Subscription();

  constructor(
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private keycloak: KeycloakService,
    private layoutService: LayoutService,
    private modalService: BsModalService,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.tenant = environment.tenant;

    this.yearNow = DateTime.now().toFormat('yyyy');

    this.setExternalLinks(this.currentLanguage);

    this.unsubOnDestroy =
      this.translateService.onLangChange.subscribe(
        (value) => {
          this.setExternalLinks(value.lang);
        }
      );

    this.isLandingPage =
      this.router.url.includes('landing');

    this.activatedRoute.queryParams.subscribe((params) => {
      this.keycloak.isLoggedIn().then((status) => {
        if (!status) {
          // This allows opening the various dialogues by query parameter.
          const {
            showImprint, // Impressum
            showTermsOfService, // AGB
            showPrivacy, // Datenschutzerklärung
            showPrivacyTerms, // Datenschutzerklärung
            showSystemRequirements, // Systemanforderungen
            showContactSdv, // Kontakt
          } = params;
          if (showImprint === 'true') {
            this.openDialog('imprint');
          } else if (showTermsOfService === 'true') {
            this.openDialog('terms-of-service');
          } else if (
            showPrivacy === 'true' ||
            showPrivacyTerms === 'true'
          ) {
            this.openDialog('privacy-terms');
          } else if (showSystemRequirements === 'true') {
            this.openDialog('system-requirements');
          } else if (
            environment.tenant === 'sdv' &&
            showContactSdv === 'true'
          ) {
            this.openDialog('contact-sdv');
          }
        }
      });
    });
  }

  openDialog(pageTemplate: InfoPageTemplate): void {
    const modalRef: BsModalRef<InfoPageDialogComponent> =
      this.modalService.show(InfoPageDialogComponent, {
        class: 'modal-lg',
        initialState: { template: pageTemplate },
      });

    modalRef.onHidden?.subscribe(() => {
      this.activatedRoute.queryParams.subscribe(
        (oldParams) => {
          const newParams = {
            ...oldParams,
            showImprint: undefined,
            showTermsOfService: undefined,
            showPrivacy: undefined,
            showPrivacyTerms: undefined,
            showSystemRequirements: undefined,
            showContactSdv: undefined,
          };

          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: newParams,
            queryParamsHandling: 'merge',
          });
        }
      );
    });
  }

  ngOnDestroy(): void {
    this.unsubOnDestroy.unsubscribe();
  }

  get currentLanguage() {
    return this.translateService.currentLang;
  }

  get isHandset$() {
    return this.layoutService.isHandset$;
  }

  get backendVersion() {
    return this.appService.versionBackend;
  }

  get versionFrontend() {
    return this.appService.versionFrontend;
  }

  private setExternalLinks(language: string) {
    if (language === 'de') {
      this.linkToContactPage =
        'https://itb-academic-tests.org/teilnehmer/teilnehmer-support/';
      this.linkToWithdrawalPage =
        'https://itb-academic-tests.org/widerrufsbelehrung/';
    } else {
      this.linkToContactPage =
        'https://itb-academic-tests.org/en/participants/support-participants/';
      this.linkToWithdrawalPage =
        'https://itb-academic-tests.org/en/instructions-withdrawal/';
    }
  }
}
